import { useState } from "react";
import { AlertTriangle, Mail, Play, RotateCcw, Zap } from "lucide-react";

import type { AutoTrip } from "../../api/client";
import { AutoArmError, type AutoModeState } from "../../hooks/useAutoMode";
import type { OperatorEntryState } from "../../hooks/useOperatorEntry";
import OperatorBuyControl from "./OperatorBuyControl";
import { cn } from "@/lib/utils";
import InfoTooltip from "@/components/common/InfoTooltip";

/**
 * LA maison du mode AUTO (un fait = une maison) : interrupteur armer/couper (armement
 * gardé phrase « ARM AUTO » + OTP e-mail), état en pause (raison + reprise + bouton
 * « Reprendre maintenant » deux-clics, owner 2026-09-01), carte de trip, note de
 * dead-band. Rendue dans le lanceur (VEILLE, via CockpitCommand) ET dans
 * la colonne droite en cycle ACTIF ({@code cycleActive}) — correctif du trou d'UX
 * 2026-07-21 : avec des cycles multi-jours, l'écran de veille est rare et l'interrupteur
 * était de fait inaccessible ; armer en cours de cycle = « enchaîne à la fin de
 * celui-ci » (l'opener backend attend un livre flat, le cycle en cours n'est jamais
 * modifié).
 */
export default function AutoModeCard({
  auto,
  armed,
  cycleActive = false,
  compact = false,
  operatorEntry,
}: {
  auto: AutoModeState;
  armed: boolean;
  /** « Acheter maintenant » hors cycle (2026-10-22) : lève la pause et ouvre un BUY. Absent ⇒ rien. */
  operatorEntry?: OperatorEntryState;
  /** True en disposition ACTIVE — ajuste la copie (« enchaînera à la fin du cycle »). */
  cycleActive?: boolean;
  /**
   * Rail droit en cycle ACTIF : une seule rangée (icône + titre + interrupteur), la note
   * dead-band bascule en infobulle (i). Les états transitoires (armement, trip) restent
   * déployés — on ne compacte que le repos.
   */
  compact?: boolean;
}) {
  const [arming, setArming] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [resumeConfirming, setResumeConfirming] = useState(false);
  const [resumeErr, setResumeErr] = useState<string | null>(null);

  const trip: AutoTrip | null = auto.status?.lastTrip ?? null;
  const pips = auto.status?.lossThresholdPips;
  const lossLimit = auto.status?.consecutiveLossLimit;
  const pullback = auto.status?.resumePullbackPts ?? 0;
  const winPullback = auto.status?.winPullbackPts ?? 0;
  const paused = auto.status?.paused === true;
  const pauseReason = auto.status?.pauseReason ?? null;
  const resumeAt = auto.status?.resumeAt ?? null;
  const pauseLabel =
    pauseReason === "FLATTEN_CLOSE"
      ? "Flatten calendrier en perte"
      : pauseReason === "WIN_PULLBACK"
        ? "Attente post-gain"
        : "Pertes consécutives";

  const confirmResume = async () => {
    setResumeErr(null);
    try {
      await auto.resume();
    } catch {
      setResumeErr("Reprise impossible — réessaie (la pause est peut-être déjà levée).");
    } finally {
      setResumeConfirming(false);
    }
  };

  const beginArm = async () => {
    setErr(null);
    setArming(true);
    setOtpSent(false);
    try {
      await auto.requestOtp();
      setOtpSent(true);
    } catch {
      setErr("Impossible d'envoyer le code par e-mail. Réessaie.");
    }
  };

  const confirmArm = async () => {
    setErr(null);
    try {
      await auto.arm(phrase.trim(), otp.trim());
      resetForm();
    } catch (e) {
      setErr(e instanceof AutoArmError ? e.detail : "Impossible d'armer AUTO.");
    }
  };

  const resetForm = () => {
    setArming(false);
    setPhrase("");
    setOtp("");
    setOtpSent(false);
    setErr(null);
  };

  const toggle = () => {
    if (armed) {
      void auto.disarm();
    } else if (!arming) {
      void beginArm();
    }
  };

  const tone = armed && paused ? "paused" : armed ? "armed" : trip ? "tripped" : "off";

  // La note s'adapte aux DEUX knobs (L, pull-back) — « 1 cycle perdant consécutif » était
  // bancal et « jusqu'à la réouverture » ignorait la reprise au pull-back (relevé owner 22/07).
  const deadbandNote = (
    <span data-testid="auto-deadband-note">
      {armed ? "Se met en pause" : "Une fois actif, se met en pause"} (+ e-mail) après{" "}
      <strong className="text-foreground">
        {lossLimit == null
          ? "…"
          : lossLimit > 1
            ? `${lossLimit} cycles perdants consécutifs`
            : "un cycle perdant"}
      </strong>{" "}
      (perdant = ferme sous <strong className="text-foreground">−{pips ?? "…"} pts par position</strong> ;
      les flattens calendrier ne comptent pas) — reprise{" "}
      {pullback > 0 ? (
        <>
          dès un repli de <strong className="text-foreground">{pullback} pts</strong> sous le prix de
          sortie, ou à la réouverture (premier des deux).
        </>
      ) : (
        "à la réouverture du marché."
      )}
      {winPullback > 0 && (
        <>
          {" "}Après un <strong className="text-foreground">gain</strong> : attend un repli de{" "}
          <strong className="text-foreground">{winPullback} pts</strong> sous le prix de sortie avant
          le cycle suivant (ou la réouverture, premier des deux).
        </>
      )}
    </span>
  );

  return (
    <div
      className={cn(
        "flex flex-col rounded-xl border transition-colors",
        compact ? "gap-2.5 p-3" : "gap-3 p-4",
        tone === "armed" && "border-accent-neon/45 bg-accent-neon/5",
        tone === "paused" && "border-warning/45 bg-warning-soft",
        tone === "tripped" && "border-loss/40 bg-loss-soft",
        tone === "off" && "border-border bg-background/30",
      )}
      data-testid="auto-card"
      data-tone={tone}
    >
      {/* rangée d'en-tête : titre + interrupteur */}
      <div className={cn("flex items-center", compact ? "gap-2.5" : "gap-3")}>
        <div
          className={cn(
            "grid shrink-0 place-items-center rounded-lg border",
            compact ? "size-7" : "size-9",
            armed ? "border-accent-neon/40 bg-accent-neon/10 text-accent-neon" : "border-border text-muted-foreground",
          )}
        >
          <Zap className={compact ? "size-4" : "size-5"} aria-hidden />
        </div>
        <div className="min-w-0">
          <div className="text-foreground flex items-center gap-2 font-semibold">
            Mode AUTO
            {compact && <InfoTooltip label="Règle de pause AUTO">{deadbandNote}</InfoTooltip>}
            {armed && !paused && (
              <span className="text-accent-neon inline-flex items-center gap-1 text-xs font-medium">
                <span className="bg-accent-neon size-1.5 animate-pulse rounded-full" /> actif
              </span>
            )}
            {armed && paused && (
              <span className="text-warning inline-flex items-center gap-1 text-xs font-medium" data-testid="auto-paused-chip">
                <span className="bg-warning size-1.5 rounded-full" /> en pause
              </span>
            )}
          </div>
          <div className="text-muted-foreground text-xs">
            {armed && paused
              ? `${pauseLabel} — ${
                  pauseReason === "CONSECUTIVE_LOSSES" && pullback > 0
                    ? `reprise au repli de ${pullback} pts ou à la réouverture`
                    : pauseReason === "WIN_PULLBACK" && winPullback > 0
                      ? `reprise au repli de ${winPullback} pts ou à la réouverture`
                      : "reprise à la réouverture"
                }${resumeAt ? ` (~${formatResume(resumeAt)})` : ""}.`
              : armed
                ? cycleActive
                  ? "Enchaînera dès la fin du cycle en cours."
                  : "Ouvre des cycles BUY automatiquement."
                : "long · champion · enchaîne les cycles"}
          </div>
        </div>
        <div className="ml-auto">
          <Switch on={armed} onClick={toggle} disabled={auto.pending} label="Mode AUTO" />
        </div>
      </div>

      {/* « Reprendre maintenant » (owner 2026-09-01) : lève la pause en deux clics — sans OTP,
          la fenêtre armée l'a déjà payé ; l'ouvreur repart via les gates normales. Rendu dans
          les deux dispositions (compacte incluse) — fin du piège « pause dodo jusqu'à dimanche ». */}
      {armed && paused && !arming && (
        <div className="flex flex-col gap-1.5">
          {resumeConfirming ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void confirmResume()}
                disabled={auto.pending}
                className="bg-warning flex-1 rounded-lg px-3 py-1.5 text-xs font-bold text-black disabled:opacity-50"
                data-testid="auto-resume-confirm"
              >
                {auto.pending ? "Reprise…" : `Confirmer — lève « ${pauseLabel} », BUY via les gates normales`}
              </button>
              <button
                type="button"
                onClick={() => setResumeConfirming(false)}
                className="border-border text-muted-foreground hover:text-foreground rounded-lg border px-3 py-1.5 text-xs"
              >
                Annuler
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setResumeErr(null);
                setResumeConfirming(true);
              }}
              className="text-warning border-warning/45 hover:bg-warning/10 inline-flex w-fit items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium"
              data-testid="auto-resume-now"
            >
              <Play className="size-3.5" aria-hidden /> Reprendre maintenant
            </button>
          )}
          {resumeErr && (
            <p className="text-loss text-xs" role="alert">
              {resumeErr}
            </p>
          )}
        </div>
      )}

      {/* « Acheter maintenant » hors cycle (2026-10-22) : lève la pause éventuelle PUIS ouvre un cycle
          BUY tout de suite — deux clics, sans OTP ; la fenêtre 60 min avant clôture reste respectée. */}
      {armed && !cycleActive && !arming && operatorEntry && (
        <OperatorBuyControl entry={operatorEntry} variant="auto" />
      )}

      {/* carte de trip (en pause / coupé après une perte) */}
      {trip && !armed && !arming && (
        <div className="border-loss/30 rounded-lg border bg-card p-3" data-testid="auto-tripped">
          <div className="text-loss flex items-center gap-2 text-sm font-bold">
            <AlertTriangle className="size-4" aria-hidden /> Coupé — cycle perdant
          </div>
          <dl className="mt-2 grid grid-cols-2 gap-y-1 text-sm tabular-nums">
            <dt className="text-muted-foreground">Cycle</dt>
            <dd className="text-right font-mono">#{trip.cycleId}</dd>
            <dt className="text-muted-foreground">Clôturé à</dt>
            <dd className="text-loss text-right font-mono font-semibold">{trip.pnlEur.toFixed(2)} €</dd>
            <dt className="text-muted-foreground">Le</dt>
            <dd className="text-right font-mono text-xs">{new Date(trip.at).toLocaleString()}</dd>
          </dl>
          <div className="border-border text-muted-foreground mt-2 flex items-center gap-2 border-t pt-2 text-xs">
            <Mail className="text-accent-neon size-3.5 shrink-0" aria-hidden /> E-mail de notification envoyé.
          </div>
          <button
            type="button"
            onClick={() => void beginArm()}
            className="text-loss border-loss/40 hover:bg-loss/10 mt-3 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium"
            data-testid="auto-rearm"
          >
            <RotateCcw className="size-3.5" aria-hidden /> Réarmer AUTO
          </button>
        </div>
      )}

      {/* flux de confirmation d'armement */}
      {arming ? (
        <div className="border-accent-neon/30 flex flex-col gap-2.5 rounded-lg border bg-card p-3">
          <p className="text-muted-foreground text-sm">
            Armement du trading automatique. Tape <strong className="text-accent-neon">ARM AUTO</strong> et le code
            reçu par e-mail.
          </p>
          {cycleActive && (
            <p className="text-muted-foreground text-xs" data-testid="auto-chain-note">
              Prendra effet dès que le livre est flat — le cycle en cours n'est pas modifié.
            </p>
          )}
          <input
            type="text"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder="ARM AUTO"
            aria-label="Phrase de confirmation"
            className="bg-background border-border focus:border-accent-neon rounded-lg border px-3 py-2 text-sm outline-none"
          />
          <input
            type="text"
            inputMode="numeric"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder={otpSent ? "code à 6 chiffres reçu par e-mail" : "demande du code…"}
            aria-label="Code à usage unique"
            className="bg-background border-border focus:border-accent-neon rounded-lg border px-3 py-2 font-mono text-sm outline-none"
          />
          {err && (
            <p className="text-loss text-sm" role="alert">
              {err}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={confirmArm}
              disabled={auto.pending || phrase.trim() === "" || otp.trim() === ""}
              className="bg-accent-neon flex-1 rounded-lg px-4 py-2 text-sm font-bold text-black disabled:opacity-50"
              data-testid="auto-confirm-arm"
            >
              {auto.pending ? "Armement…" : "Armer AUTO"}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="border-border text-muted-foreground hover:text-foreground rounded-lg border px-4 py-2 text-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      ) : (
        tone !== "tripped" &&
        !compact && (
          <div className="border-border/70 text-muted-foreground flex items-start gap-2 rounded-lg border border-dashed px-3 py-2.5 text-xs">
            <AlertTriangle className="text-warning mt-0.5 size-4 shrink-0" aria-hidden />
            {deadbandNote}
          </div>
        )
      )}
    </div>
  );
}

/** Interrupteur piste+pouce (role=switch). Actif = accent-neon. */
function Switch({
  on,
  onClick,
  disabled,
  label,
}: {
  on: boolean;
  onClick: () => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      data-testid="auto-switch"
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-neon disabled:opacity-50",
        on ? "bg-accent-neon" : "bg-muted",
      )}
    >
      <span
        className={cn(
          "inline-block size-5 transform rounded-full bg-white shadow transition-transform",
          on ? "translate-x-[1.375rem]" : "translate-x-0.5",
        )}
      />
    </button>
  );
}

function formatResume(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString("fr-FR", {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
